<template>
  <div class="push-notification-toggle">
    <label class="toggle-switch">
      <input
        type="checkbox"
        v-model="isSubscribed"
        :disabled="!isSupported || loading"
        @change="toggleSubscription"
      />
      <span class="slider"></span>
      <span class="toggle-label">
        <span v-if="loading" class="spinner"></span>
        <span :class="['icon', iconClass]">{{ icon }}</span>
        {{ label }}
      </span>
    </label>

    <div v-if="error" class="alert alert-error">
      {{ error }}
      <button @click="error = null" class="close-btn">&times;</button>
    </div>

    <div v-if="!isSupported" class="alert alert-warning">
      Push notifications are not supported in this browser.
    </div>

    <div v-if="permissionDenied" class="alert alert-info">
      Notifications are blocked. Please enable them in your browser settings.
    </div>
  </div>
</template>

<script>
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribedToPush,
  getCurrentSubscription
} from "@/api/PushNotifications.js";

export default {
  name: "PushNotificationToggle",

  props: {
    departmentCode: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      isSubscribed: false,
      isSupported: false,
      permissionDenied: false,
      loading: false,
      error: null
    };
  },

  computed: {
    label() {
      if (!this.isSupported) {
        return "Unavailable";
      }
      if (this.permissionDenied) {
        return "Blocked";
      }
      return this.isSubscribed ? "Notifications ON" : "Notifications OFF";
    },

    icon() {
      if (!this.isSupported || this.permissionDenied) {
        return "🔕";
      }
      return this.isSubscribed ? "🔔" : "🔕";
    },

    iconClass() {
      if (!this.isSupported || this.permissionDenied) {
        return "disabled";
      }
      return this.isSubscribed ? "active" : "";
    }
  },

  async mounted() {
    this.isSupported = isPushSupported();

    if (this.isSupported) {
      const permission = getNotificationPermission();
      this.permissionDenied = permission === "denied";

      if (permission !== "denied") {
        this.isSubscribed = await isSubscribedToPush();
      }
    }
  },

  methods: {
    async toggleSubscription() {
      this.loading = true;
      this.error = null;
      const newValue = this.isSubscribed;

      try {
        if (newValue) {
          await subscribeToPush(this.departmentCode);
          this.$emit("subscribed");
        } else {
          await unsubscribeFromPush();
          this.$emit("unsubscribed");
        }
      } catch (err) {
        console.error("Push subscription error:", err);
        this.error = err.message || "Failed to update notification settings";
        // Revert the switch
        this.isSubscribed = !newValue;

        // Check if permission was denied
        if (err.message?.includes("denied")) {
          this.permissionDenied = true;
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.push-notification-toggle {
  display: flex;
  align-items: center;
  padding: 0 10px;
}

.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-switch input {
  display: none;
}

.slider {
  width: 36px;
  height: 20px;
  background-color: #ccc;
  border-radius: 20px;
  position: relative;
  transition: background-color 0.3s;
  margin-right: 8px;
}

.slider::before {
  content: "";
  position: absolute;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
}

input:checked + .slider {
  background-color: #4caf50;
}

input:checked + .slider::before {
  transform: translateX(16px);
}

input:disabled + .slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: white;
}

.icon {
  margin-right: 4px;
}

.icon.active {
  animation: ring 0.5s ease;
}

.icon.disabled {
  opacity: 0.5;
}

@keyframes ring {
  0%,
  100% {
    transform: rotate(0);
  }
  25% {
    transform: rotate(15deg);
  }
  75% {
    transform: rotate(-15deg);
  }
}

.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 4px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.alert {
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 10px;
  display: flex;
  align-items: center;
}

.alert-error {
  background-color: #f44336;
  color: white;
}

.alert-warning {
  background-color: #ff9800;
  color: white;
}

.alert-info {
  background-color: #2196f3;
  color: white;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  margin-left: 8px;
  cursor: pointer;
}
</style>
